/**
 * draftStorage — privacy-conscious draft store.
 *
 * Backed by `sessionStorage` (NOT localStorage) so the user's half-written
 * task titles, payment notes, reading URLs, and other business-content
 * drafts auto-clear when the browser tab closes. This is critical for
 * shared-workstation scenarios (a lawyer/consultant using a client's
 * machine should not leave drafts behind).
 *
 * One-time migration: if a legacy `polkdesq_draft_*` entry exists in
 * `localStorage`, it is read once at module load, copied into
 * sessionStorage, and then removed from localStorage. This preserves the
 * draft for the current session and stops the long-term leakage.
 *
 * API mirrors a tiny subset of the Storage interface so the caller code
 * doesn't need a refactor.
 */
import { logger } from './logger';

const PREFIX = 'polkdesq_draft_';

function probeStorage() {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      // Smoke-test sessionStorage availability.
      const key = '__pk_draftStorage_probe__';
      window.sessionStorage.setItem(key, '1');
      window.sessionStorage.removeItem(key);
      return window.sessionStorage;
    }
  } catch (err) {
    // Safari private mode or quota exceeded — drafts simply don't persist.
    logger.warn('draftStorage: sessionStorage unavailable, drafts will not persist:', err?.message);
  }
  return null;
}

const storage = probeStorage();

// One-time migration from legacy localStorage drafts.
(function migrateLegacy() {
  if (!storage) return;
  try {
    const local = window.localStorage;
    if (!local) return;
    const toMigrate = [];
    for (let i = 0; i < local.length; i += 1) {
      const key = local.key(i);
      if (key && key.startsWith(PREFIX)) toMigrate.push(key);
    }
    toMigrate.forEach((key) => {
      try {
        const value = local.getItem(key);
        if (value && !storage.getItem(key)) storage.setItem(key, value);
        local.removeItem(key);
      } catch (err) {
        logger.warn(`draftStorage: failed to migrate legacy key "${key}":`, err?.message);
      }
    });
  } catch (err) {
    logger.warn('draftStorage: legacy localStorage unavailable, migration skipped:', err?.message);
  }
})();

export function getDraftItem(key) {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch (err) {
    logger.warn(`draftStorage.get("${key}") failed:`, err?.message);
    return null;
  }
}

export function setDraftItem(key, value) {
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch (err) {
    logger.warn(`draftStorage.set("${key}") failed:`, err?.message);
    return false;
  }
}

export function removeDraftItem(key) {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch (err) {
    logger.warn(`draftStorage.remove("${key}") failed:`, err?.message);
  }
}

export function listDraftKeys() {
  if (!storage) return [];
  const keys = [];
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const k = storage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
  } catch (err) {
    logger.warn('draftStorage.listKeys() failed:', err?.message);
  }
  return keys;
}

export const DRAFT_PREFIX = PREFIX;
